import { Injectable } from '@angular/core';
import { interval, Observable, of, Subscription } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { AuthProxyService } from '../auth-proxy/auth-proxy.service';
import { SessionManagerService, UserSession } from '../session-manager/session-manager.service';

const TICK_INTERVAL_MS = 60000;

export const LOGIN_ERROR_TOKENS = {
    LOCKED: 'LOCKED',
    CHANGE: 'CHANGE',
    PERMISSION: 'PERMISSION',
    NOT_AUTHORIZED: 'NOT_AUTHORIZED',
    WRONG_CREDENTIALS: 'WRONG_CREDENTIALS'
} as const;

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
    private tickSubscription: Subscription | null = null;
    private isLoggingOut = false;

    constructor(
        private authProxyService: AuthProxyService,
        private sessionManager: SessionManagerService,
    ) {}

    login(username: string, password: string): Observable<string> {
        return new Observable(observer => {
            this.authProxyService.login(username, password).subscribe({
                next: token => {
                    if (token && !this.isErrorToken(token)) {
                        const user: UserSession = {
                            Token: token,
                            UserID: 0,
                            Username: username,
                            Email: ''
                        };
                        this.sessionManager.setUserInLocalStorage(user);
                        this.loginFromLocalStorage();
                        observer.next('');
                    } else {
                        observer.next(this.handleLoginError(token));
                    }
                    observer.complete();
                },
                error: () => {
                    observer.next(this.handleLoginError(null));
                    observer.complete();
                }
            });
        });
    }

    inspectorPortalLogin(username: string, password: string): Observable<string> {
        return new Observable(observer => {
            this.authProxyService.inspectorPortalLogin(username, password).subscribe({
                next: result => {
                    if (result.HasAccess && result.Token) {
                        const user: UserSession = {
                            Token: result.Token,
                            UserID: 0,
                            Username: username,
                            Email: '',
                            IsAdmin: result.IsAdmin,
                            RequiresPasswordChange: result.RequiresPasswordChange ?? false
                        };
                        this.sessionManager.setUserInLocalStorage(user);
                        if (!result.RequiresPasswordChange) {
                            this.loginFromLocalStorage();
                        } else {
                            this.sessionManager.setCurrentUser(user);
                        }
                        observer.next(result.RequiresPasswordChange ? LOGIN_ERROR_TOKENS.CHANGE : '');
                    } else {
                        observer.next('NOT_AUTHORIZED');
                    }
                    observer.complete();
                },
                error: () => {
                    observer.next(this.handleLoginError(null));
                    observer.complete();
                }
            });
        });
    }

    vehicleImportFormLogin(username: string, password: string): Observable<string> {
        return new Observable(observer => {
            this.authProxyService.vehicleImportFormLogin(username, password).subscribe({
                next: result => {
                    if (result.HasAccess && result.Token) {
                        const user: UserSession = {
                            Token: result.Token,
                            UserID: 0,
                            Username: username,
                            Email: '',
                            IsAdmin: result.IsAdmin,
                            RequiresPasswordChange: result.RequiresPasswordChange ?? false,
                        };
                        this.sessionManager.setUserInLocalStorage(user);
                        if (!result.RequiresPasswordChange) {
                            this.loginFromLocalStorage();
                        } else {
                            this.sessionManager.setCurrentUser(user);
                        }
                        observer.next(result.RequiresPasswordChange ? LOGIN_ERROR_TOKENS.CHANGE : '');
                    } else if (result.ErrorCode === LOGIN_ERROR_TOKENS.LOCKED) {
                        observer.next(LOGIN_ERROR_TOKENS.LOCKED);
                    } else if (result.ErrorCode === LOGIN_ERROR_TOKENS.WRONG_CREDENTIALS || !result.Token) {
                        observer.next(LOGIN_ERROR_TOKENS.WRONG_CREDENTIALS);
                    } else {
                        observer.next(LOGIN_ERROR_TOKENS.NOT_AUTHORIZED);
                    }
                    observer.complete();
                },
                error: () => {
                    observer.next(LOGIN_ERROR_TOKENS.WRONG_CREDENTIALS);
                    observer.complete();
                }
            });
        });
    }

    loginFromLocalStorage(): void {
        const userSession = this.sessionManager.getUserSession();
        this.sessionManager.setCurrentUser(userSession);
        if (userSession) {
            this.startTick();
        }
    }

    isUserLoggedIn(): Observable<boolean> {
        const userSession = this.sessionManager.getUserSession();
        this.sessionManager.setCurrentUser(userSession);
        if (!userSession) {
            return of(false);
        }

        return this.authProxyService.tick(userSession.Token).pipe(
            catchError(() => {
                this.performLogoutCleanup();
                return of(false);
            })
        );
    }

    logout(): void {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;
        this.stopTick();

        const userSession = this.sessionManager.getUserSession();
        if (userSession?.Token) {
            this.authProxyService.logout(userSession.Token).pipe(
                catchError(() => of(null)),
                finalize(() => this.performLogoutCleanup())
            ).subscribe();
        } else {
            this.performLogoutCleanup();
        }
    }

    private performLogoutCleanup(): void {
        this.sessionManager.removeCurrentUser();
        this.stopTick();
        this.isLoggingOut = false;
        const locale = localStorage.getItem('locale') || 'en';
        window.location.href = `/${locale}/`;
    }

    private startTick(): void {
        if (this.tickSubscription) {
            return;
        }
        if (!this.sessionManager.getUserSession()?.Token) {
            return;
        }
        this.tickSubscription = interval(TICK_INTERVAL_MS).pipe(
            switchMap(() => {
                const session = this.sessionManager.getUserSession();
                if (!session?.Token) {
                    return of(false);
                }
                return this.authProxyService.tick(session.Token).pipe(
                    catchError(error => {
                        console.warn('Tick request failed:', error?.status);
                        return of(null);
                    })
                );
            })
        ).subscribe(isValid => {
            if (isValid === false || isValid === null) {
                this.handleSessionExpired();
            }
        });
    }

    private stopTick(): void {
        this.tickSubscription?.unsubscribe();
        this.tickSubscription = null;
    }

    private handleSessionExpired(): void {
        if (this.isLoggingOut) {
            return;
        }
        this.isLoggingOut = true;
        this.performLogoutCleanup();
    }

    private isErrorToken(token: string): boolean {
        return Object.values(LOGIN_ERROR_TOKENS).includes(token as any);
    }

    private handleLoginError(token: string | null): string {
        switch (token) {
            case LOGIN_ERROR_TOKENS.LOCKED:
                return 'Account locked. Contact administrator.';
            case LOGIN_ERROR_TOKENS.CHANGE:
                return 'CHANGE';
            case LOGIN_ERROR_TOKENS.PERMISSION:
                return 'NOT_AUTHORIZED';
            default:
                return LOGIN_ERROR_TOKENS.WRONG_CREDENTIALS;
        }
    }
}
